import React, { Suspense, lazy } from 'react';
import type { CustomField } from '@puckeditor/core';

export type ProductPickerFieldProps = {
  field: { label?: string };
  name: string;
  readOnly?: boolean;
};

// Lazy so `@apollo/client`, the generated product-search document, and `ReactSelect` stay out of
// the storefront bundle. `carousel-types.ts` (and through it `product-carousel.tsx`) is reachable
// from the production renderer, so a static import would ship an Apollo client to every shopper
// viewing a published CMS page even though only the editor can render this field. Mirrors
// `brand-picker-field.tsx`; `bundle-boundaries.test.ts`'s static-only walk is what enforces it.
const ProductPickerWidget = lazy(() => import('./product-picker-widget'));

function ProductPickerFieldRender(props: ProductPickerFieldProps): JSX.Element {
  return (
    <Suspense fallback={null}>
      <ProductPickerWidget {...props} />
    </Suspense>
  );
}

/**
 * Puck `custom` field for hand-picking products in an explicit order.
 *
 * Bound to `productIds` (an ordered array). On every change the widget also writes
 * `productNames` in the same dispatch so the chosen list can be listed back to the author without
 * a second query, which matters because the carousel's whole purpose is the author's sequence.
 *
 * `ai.exclude` because the product list is per-store live data the AI plugin cannot enumerate, and
 * an ordering is an editorial decision rather than something to generate.
 */
export const productPickerField: CustomField<string[]> = {
  type: 'custom',
  label: 'Ordered products',
  render: ProductPickerFieldRender as CustomField<string[]>['render'],
  ai: { exclude: true },
};
